import React, { useEffect, useState, useRef } from 'react';
import { Form, InputGroup, Image, Button } from 'react-bootstrap';
import { useSearchSuggestionsQuery } from 'services';
import useDebounce from 'hooks/useDebounce';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from 'store/auth';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { S3_URL, USER_TYPES } from 'constants/index';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const SearchSuggestions: React.FC<any> = () => {
	const { translate } = useTranslate();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const [isFocused, setIsFocused] = useState(false);
	const [locationQry, setLocationQry] = useState<string | null>(null);
	const debouncedQry = useDebounce(locationQry, 300);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const currentPath = location.pathname;

	const searchStr = `page=1&limit=10&keyword=${locationQry?.replace(/^#/, '')}&searchType=${locationQry?.startsWith('#') ? 'hashtag' : ''}`;
	const { data: { docs = [] } = {}, refetch } = useSearchSuggestionsQuery({
		searchStr,
		keyword: locationQry?.replace(/^#/, ''),
	});

	useEffect(() => {
		if (debouncedQry !== null && isFocused) {
			refetch();
		}
	}, [debouncedQry, isFocused]);

	useEffect(() => {
		if (searchParams.get('keyword')) {
			setLocationQry(searchParams.get('keyword'));
		}
	}, []);

	const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			setIsFocused(false); // Removed the setTimeout as it's unnecessary
			const value = (e.target as HTMLInputElement).value.trim().replace(/\s+/g, ' ');

			// Handle empty value
			if (!value) {
				console.warn('Search value is empty');
			}

			try {
				navigate(`/${user?.userType}/members-result?keyword=${encodeURIComponent(value)}`);
			} catch (error) {
				console.error('Navigation error:', error);
			}
		}
	};

	const onViewAllResult = () => {
		const value = locationQry?.trim().replace(/\s+/g, ' ').replace(/\s+$/, '');
		navigate(`/${user?.userType}/members-result?keyword=${encodeURIComponent(value || '')}`);
		setTimeout(() => {
			setIsFocused(false);
		}, 100);
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.length === 0 ? '' : e.target.value;
		setLocationQry(value);
	};

	return (
		<InputGroup className="nav-search">
			<i className="ri-search-line nav-search-icon position-absolute"></i>
			<Form.Control
				type="text"
				name="keyword"
				placeholder={translate('PlaceHolder.multiSearchPlaceHolder')}
				className="rounded-pill"
				defaultValue={
					!currentPath?.includes('/job-search') ? searchParams.get('keyword') || '' : ''
				}
				onKeyDown={handleSearch}
				onChange={handleOnChange}
				onFocus={() => setIsFocused(true)}
				onMouseUp={() => setIsFocused(true)}
				onBlur={(e: any) => {
					e.preventDefault();
					setTimeout(() => setIsFocused(false), 500);
				}}
				autoComplete="off"
			/>

			{isFocused ? (
				<div className="nav-search-options position-absolute bg-white w-100" ref={dropdownRef}>
					<div className="nav-search-options-result">
						{docs?.length > 0 ? (
							docs?.map((data: any, index: number) => (
								<a
									key={index}
									className="searched-user d-flex align-items-center gap-3"
									onClick={(e: any) => {
										e.stopPropagation();
										if (data?.isHashtag) {
											navigate(`/hashtag/Profile/${data?.id}`);
										} else {
											navigate(`/${data?.userType}/profile/${data?.userId}`);
										}
									}}
								>
									<div className="searched-user-img">
										<Image
											src={
												data?.isHashtag
													? IMAGE_PATHS.Hashtag
													: data?.profilePicture
														? `${S3_URL}${data.profilePicture}`
														: IMAGE_PATHS.UserDefault
											}
											className="rounded-circle h-100 w-100 object-fit-cover"
											loading="lazy"
										/>
									</div>
									<div className="searched-user-detail flex-1">
										<h6 className="mb-0 fw-bold">{data?.name}</h6>
										<p className="mb-0">
											{data?.userType === USER_TYPES?.COMPANY
												? data?.industryName
												: `${data?.designation || ''}${data?.designation && data?.companyName ? ', ' : ''}${data?.companyName || ''}`}
										</p>
										{data?.address && <p className="mb-0">{data.address}</p>}
									</div>
								</a>
							))
						) : (
							<p className="px-4 mb-0">{translate('Common.noResult')}</p>
						)}
					</div>
					{docs?.length > 0 && (
						<div className="text-center p-2 p-md-3">
							<Button
								variant=""
								onClick={(e: any) => {
									e.stopPropagation();
									onViewAllResult();
								}}
								className="view-searched-users fw-bold border-0 p-0"
							>
								{translate('Common.viewAllResult')}
							</Button>
						</div>
					)}
				</div>
			) : (
				<></>
			)}
		</InputGroup>
	);
};

export default SearchSuggestions;
